import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { Link } from 'react-router-dom';
import { BellIcon, CalendarDaysIcon, MapIcon, TruckIcon } from 'lucide-react';
import { citizenAddress, liveCollection, materials } from '../../data/collectes';
import { distanceKm, routeLengthKm } from '../../utils/geo';
const TRUCK_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>';
const HOME_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 10v10h14V10"/></svg>';
const truckIcon = L.divIcon({
  className: 'collection-truck',
  html: `<span class="grid h-9 w-9 place-items-center rounded-xl border-2 border-white bg-ville-blue shadow-lg shadow-ville-navy/30">${TRUCK_SVG}</span>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18]
});
const homeIcon = L.divIcon({
  className: 'collection-home',
  html: `<span class="grid h-8 w-8 place-items-center rounded-full border-[3px] border-white bg-ville-navy shadow-md shadow-ville-navy/30">${HOME_SVG}</span>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16]
});
const AVERAGE_SPEED_KMH = 12;
const TICK_MS = 700;
const PROGRESS_PER_TICK = 0.004;

/** Cumulative lengths, to interpolate a position from a percentage of the route. */
function cumulativeLengths(points: [number, number][]): number[] {
  const totals = [0];
  for (let i = 1; i < points.length; i += 1) {
    totals.push(totals[i - 1] + distanceKm(points[i - 1], points[i]));
  }
  return totals;
}
function pointAt(points: [number, number][], totals: number[], progress: number): [number, number] {
  const target = totals[totals.length - 1] * progress;
  const index = totals.findIndex(value => value >= target);
  if (index <= 0) return points[0];
  const previous = totals[index - 1];
  const ratio = (target - previous) / Math.max(totals[index] - previous, 1e-9);
  const [lat1, lng1] = points[index - 1];
  const [lat2, lng2] = points[index];
  return [lat1 + (lat2 - lat1) * ratio, lng1 + (lng2 - lng1) * ratio];
}
function splitRoute(points: [number, number][], totals: number[], progress: number) {
  const target = totals[totals.length - 1] * progress;
  const cut = Math.max(1, totals.findIndex(value => value >= target));
  const truck = pointAt(points, totals, progress);
  return {
    done: [...points.slice(0, cut), truck] as [number, number][],
    remaining: [truck, ...points.slice(cut)] as [number, number][],
    truck
  };
}
export function CollectionTracker() {
  const {
    route: fallbackRoute,
    home,
    startProgress,
    sector,
    truck,
    startedAt,
    material
  } = liveCollection;
  const [route, setRoute] = useState<[number, number][]>(fallbackRoute);
  const [progress, setProgress] = useState(startProgress);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const totals = useMemo(() => cumulativeLengths(route), [route]);
  const {
    done,
    remaining,
    truck: truckPosition
  } = useMemo(() => splitRoute(route, totals, progress), [route, totals, progress]);
  const distance = routeLengthKm(remaining);
  const minutes = Math.max(1, Math.round(distance / AVERAGE_SPEED_KMH * 60));
  const arrived = progress >= 0.999;
  const arrivingSoon = minutes <= 10;

  /** Real route over the road network (OSRM, no API key). */
  useEffect(() => {
    const start = fallbackRoute[0];
    const url = `https://router.project-osrm.org/route/v1/driving/${start[1]},${start[0]};${home[1]},${home[0]}?overview=full&geometries=geojson`;
    let cancelled = false;
    fetch(url).then(response => response.ok ? response.json() : Promise.reject(new Error('routing'))).then((data: {
      routes?: {
        geometry: {
          coordinates: [number, number][];
        };
      }[];
    }) => {
      const coordinates = data.routes?.[0]?.geometry?.coordinates;
      if (cancelled || !coordinates || coordinates.length < 2) return;
      setRoute(coordinates.map(([lng, lat]) => [lat, lng] as [number, number]));
    }).catch(() => {
      /* Keep the approximate route if the routing service is unavailable. */
    });
    return () => {
      cancelled = true;
    };
  }, [fallbackRoute, home]);
  useEffect(() => {
    if (arrived) return;
    const timer = window.setInterval(() => {
      setProgress(current => Math.min(1, current + PROGRESS_PER_TICK));
    }, TICK_MS);
    return () => window.clearInterval(timer);
  }, [arrived]);
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: home,
      zoom: 14,
      zoomControl: false,
      scrollWheelZoom: false,
      attributionControl: true
    });
    map.attributionControl.setPrefix(false);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(map);
    L.control.zoom({
      position: 'topright'
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, [home]);
  useEffect(() => {
    if (!mapRef.current || route.length < 2) return;
    mapRef.current.fitBounds(L.latLngBounds(route), {
      padding: [34, 34]
    });
  }, [route]);
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    L.polyline(done, {
      color: '#94a3b8',
      weight: 4,
      opacity: 0.65
    }).addTo(layer);
    L.polyline(remaining, {
      color: '#005151',
      weight: 5,
      dashArray: '1 9',
      lineCap: 'round'
    }).addTo(layer);
    L.marker(home, {
      icon: homeIcon,
      title: 'Your address',
      alt: 'Your address'
    }).addTo(layer);
    L.marker(truckPosition, {
      icon: truckIcon,
      title: truck,
      alt: `Position of ${truck}`
    }).bindTooltip(truck, {
      direction: 'top',
      offset: [0, -18]
    }).addTo(layer);
  }, [done, remaining, truckPosition, home, truck]);
  return <section aria-labelledby="collection-tracker-title" className="relative isolate z-0 flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white" data-id="element-652" data-mp="3oxoo2guf9aqd">
      <div className="flex items-start justify-between gap-3 px-5 pb-4 pt-5" data-id="element-653" data-mp="3tu5czd9g3t4i">
        <div className="min-w-0" data-id="element-654" data-mp="3fyl2n67s8j8p">
          <h2 id="collection-tracker-title" className="flex items-center gap-2 text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-655" data-mp="3kvjv7kbssi4s">
            <TruckIcon className="h-5 w-5 text-ville-blue" aria-hidden="true" data-id="element-656" data-mp="1e3aocn91gy5y" />
            Collection under way
          </h2>
          <p className="mt-0.5 truncate text-sm text-slate-500" data-id="element-657" data-mp="1t85dt33rpx9w">
            {materials[material].label} · {sector} · {truck}
          </p>
        </div>
        <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${arrivingSoon ? 'bg-ville-mist text-ville-deep' : 'bg-slate-100 text-slate-600'}`} data-id="element-658" data-mp="1oavhd3hyolck">
          {arrived ? 'On site' : arrivingSoon ? 'Arriving soon' : 'On the way'}
        </span>
      </div>

      <div ref={containerRef} role="application" aria-label={`Route of ${truck} to your address`} className="h-[260px] w-full bg-ville-mist sm:h-[300px]" data-id="element-659" data-mp="2bx20aksxblhk" />

      <div className="grid grid-cols-2 divide-x divide-slate-100 border-t border-slate-100" data-id="element-660" data-mp="2kyryqal83kou">
        <div className="px-5 py-4 text-center" data-id="element-661" data-mp="2gyhmig6u7y1j">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-662" data-mp="qlacqs0guvvx">Estimated arrival</p>
          <p className="mt-1 text-2xl font-extrabold tabular-nums text-ville-blue" data-id="element-663" data-mp="mkgjrk862qw6">
            {arrived ? 'On site' : `${minutes} min`}
          </p>
        </div>
        <div className="px-5 py-4 text-center" data-id="element-664" data-mp="1b2sjjsg52rfz">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-665" data-mp="1g36nayapjf87">Current distance</p>
          <p className="mt-1 text-2xl font-extrabold tabular-nums text-ville-navy" data-id="element-666" data-mp="1kvrahgz3yu2r">
            {distance < 0.1 ? `${Math.round(distance * 1000)} m` : `${distance.toFixed(1)} km`}
          </p>
        </div>
      </div>

      <div className="border-t border-slate-100 px-5 py-4" data-id="element-667" data-mp="s7loys08d11v">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-668" data-mp="x4lwz8gn4dvm">Your address</p>
        <p className="mt-1 text-[15px] font-bold text-ville-navy" data-id="element-669" data-mp="1167hnfzf1wgd">{citizenAddress}</p>
        <p className="mt-1 text-xs text-slate-500" data-id="element-670" data-mp="165k25u2terfs">{startedAt}</p>
      </div>

      <nav aria-label="Collection shortcuts" className="grid grid-cols-3 divide-x divide-slate-100 border-t border-slate-100" data-id="element-671" data-mp="163piej3a55da">
        {[{
        to: '/notifications/settings',
        label: 'Reminders',
        Icon: BellIcon
      }, {
        to: '/my-collections/calendar',
        label: 'Calendar',
        Icon: CalendarDaysIcon
      }, {
        to: '/map',
        label: 'Map',
        Icon: MapIcon
      }].map(({
        to,
        label,
        Icon
      }) => <Link key={label} to={to} className="flex flex-col items-center gap-1.5 px-2 py-4 text-xs font-bold text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-sand hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-672" data-mp="2ms5glm34zeeq">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-673" data-mp="23z1ucls903u5">
              <Icon className="h-5 w-5" data-id="element-674" data-mp="1z1033dko4y7l" />
            </span>
            {label}
          </Link>)}
      </nav>
    </section>;
}